"use client";

import { usePathname, useSearchParams } from "next/navigation";
import { useQueryState } from "nuqs";
import { useMemo } from "react";

import { addReturnTo, getReturnTo } from "@/utils/returnTo";

/**
 * Hook for managing returnTo URL parameters
 * @returns Object with methods to get current URL, add returnTo params, and get returnTo URL
 */
export function useReturnTo() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [returnToParam] = useQueryState("returnTo");

  /**
   * Gets the current URL (pathname + search params) for use as a returnTo destination
   */
  const getCurrentUrl = useMemo(() => {
    const params = searchParams?.toString();
    return params ? `${pathname}?${params}` : pathname;
  }, [pathname, searchParams]);

  /**
   * Adds returnTo parameter to a given URL using the current URL as the destination
   * @param url - The URL to add the returnTo parameter to
   * @returns URL with returnTo parameter
   */
  const addReturnToParam = (url: string): string => {
    return addReturnTo(url, getCurrentUrl);
  };

  /**
   * Gets the returnTo URL from query params (if it exists and is valid)
   * @param paramsToRemove - Optional array of query parameter names to remove from the URL
   * @returns The returnTo URL or null if not present/invalid
   */
  const getReturnToUrl = (paramsToRemove?: string[]): string | null => {
    return getReturnTo(returnToParam, paramsToRemove);
  };

  return {
    getCurrentUrl,
    addReturnToParam,
    getReturnToUrl,
  };
}
